<script setup>
const userStore = useUserStore();

const followListDialog = ref(false);
const users = ref([]);
const isInitialLoading = ref(false);
const isLoadingMore = ref(false);
const errorMessage = ref("");
const context = reactive({
  uid: null,
  type: "followers",
});
const nextPage = ref(1);
const hasMore = ref(true);

const dialogTitle = computed(() =>
  context.type === "following" ? "Following" : "Followers",
);

const emptyStateText = computed(() => {
  if (context.type === "following") {
    return "No following accounts to show yet.";
  }

  return "No followers to show yet.";
});

const resetListState = () => {
  users.value = [];
  nextPage.value = 1;
  hasMore.value = true;
  errorMessage.value = "";
};

const loadInitialUsers = async () => {
  resetListState();

  isInitialLoading.value = true;
  try {
    await fetchUsers({ page: 1, reset: true });
  } finally {
    isInitialLoading.value = false;
  }
};

const openDialog = async ({ uid, type = "followers" } = {}) => {
  if (!uid) {
    console.warn("FollowListDialog requires a uid to fetch data.");
    return;
  }

  context.uid = uid;
  context.type = type;
  followListDialog.value = true;

  await loadInitialUsers();
};

const closeDialog = () => {
  followListDialog.value = false;
};

const fetchUsers = async ({ page, reset = false }) => {
  if (!context.uid) {
    return 0;
  }

  errorMessage.value = "";

  try {
    const api =
      context.type === "following" ? getListFollowings : getListFollowers;

    const response = await api({
      uid: context.uid,
      page,
    });

    const items = response?.user_list || [];

    if (reset) {
      users.value = items;
    } else {
      users.value = [...users.value, ...items];
    }

    nextPage.value = page + 1;
    hasMore.value = Number(response?.num_more) > 0;

    return items.length;
  } catch (error) {
    console.error("Failed to load follow list:", error);
    errorMessage.value =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to load follow list. Please try again.";
    throw error;
  }
};

const loadMoreUsers = async ({ done }) => {
  if (!hasMore.value) {
    done("empty");
    return;
  }

  if (isLoadingMore.value || isInitialLoading.value) {
    done("loading");
    return;
  }

  isLoadingMore.value = true;
  try {
    const loaded = await fetchUsers({ page: nextPage.value });
    done(loaded > 0 && hasMore.value ? "ok" : "empty");
  } catch (error) {
    done("error");
  } finally {
    isLoadingMore.value = false;
  }
};

const handleSelectUser = async (user) => {
  if (!user || !userStore?.openUserProfileDialog) return;

  userStore.closeUserProfileDialog();
  closeDialog();

  await nextTick();

  await userStore.openUserProfileDialog(user.uid);
};

defineExpose({ openDialog, closeDialog });
</script>

<template>
  <v-dialog
    v-model="followListDialog"
    class="tw-rounded-2xl !tw-z-[2010]"
    width="420"
    scrim="#000"
    opacity="0.5"
  >
    <v-card class="!tw-rounded-2xl">
      <v-card-title class="tw-flex tw-items-center tw-justify-between">
        <span class="tw-text-lg tw-font-bold">{{ dialogTitle }}</span>
        <dialog-close-btn
          :icon="'mdi-close-circle-outline'"
          :iconSize="'32'"
          @onSubmitButton="closeDialog"
        />
      </v-card-title>

      <v-card-text class="tw-space-y-4">
        <div v-if="errorMessage" class="tw-bg-red-500/20 tw-rounded-xl tw-p-3">
          <div class="tw-text-sm tw-font-semibold tw-mb-2">
            Unable to load {{ dialogTitle.toLowerCase() }}.
          </div>
          <div class="tw-text-xs tw-mb-2">{{ errorMessage }}</div>
          <v-btn
            size="small"
            color="white"
            variant="outlined"
            @click="loadInitialUsers"
          >
            Retry
          </v-btn>
        </div>

        <div
          v-if="isInitialLoading"
          class="tw-flex tw-items-center tw-justify-center tw-py-10"
        >
          <v-progress-circular indeterminate color="secondary" />
        </div>

        <v-infinite-scroll
          v-else
          :height="420"
          class="tw-max-h-[420px]"
          mode="sentinel"
          :disabled="!hasMore || isLoadingMore"
          @load="loadMoreUsers"
        >
          <template #default>
            <v-list
              v-if="users.length"
              class="tw-bg-transparent tw-p-0 tw-overflow-auto"
            >
              <v-list-item
                v-for="(user, index) in users"
                :key="user.uid"
                class=""
                @click="handleSelectUser(user)"
              >
                <template #prepend>
                  <user-avatar :imageUrl="user.profileImageURL" class="" />
                </template>

                <v-list-item-title class="tw-text-base tw-font-semibold">
                  {{ user.firstName || "Unknown user" }}
                </v-list-item-title>
                <v-list-item-subtitle
                  v-if="user.oneLiner"
                  class="tw-text-xs tw-text-white/70"
                >
                  {{ user.oneLiner }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>

            <div v-else class="tw-text-center tw-text-sm tw-py-6">
              {{ emptyStateText }}
            </div>
          </template>

          <template #empty>
            <div class="tw-text-center tw-text-xs tw-text-white/60 tw-py-2">
              No more {{ dialogTitle.toLowerCase() }}.
            </div>
          </template>

          <template #loading>
            <div
              v-if="isLoadingMore"
              class="tw-flex tw-justify-center tw-py-3 tw-text-center"
            >
              <v-progress-circular indeterminate color="secondary" size="26" />
            </div>
          </template>
        </v-infinite-scroll>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
